// ChatApp — the chat screen below the top bar: the workspace switcher and its chats on the
// left, the conversation in the middle, the chat's files on the right. Owns the stream for
// the open chat.
//
// Props: pid (from the route, may be empty), me (account id), onWsState(sleep_state, name)
// so the top bar's orb follows the current workspace.

import { h, Component } from 'preact';
import { route } from 'preact-router';
import {
  listProjects, createProject, getProject, updateProject, archiveProject, restoreProject, deleteProject,
  sharedProjects, sendMessage, cancelTurn, answerCallback, streamProject, listWorkspaces, createWorkspace,
  listModels, setProjectModel, searchChats, projectCost,
  workspaceState, wakeWs,
} from '../api.js';
import { WorkspaceSwitcher } from './switcher.js';
import { DeleteChatDialog, ShareDialog } from './dialogs.js';
import { Feed } from './feed.js';
import { Composer } from './composer.js';
import { FilesPanel } from './files.js';
import { CreditChip, ActionCard } from './billing.js';
import { Icon } from './icons.js';
import { fromHistory, applyEvent, markAnswered } from './transcript.js';
import './ui.css';

const BUSY = new Set(['running', 'waking', 'waiting']);
const WRITE_ROLES = ['owner', 'member'];
const titleFrom = (text) => {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > 48 ? t.slice(0, 46).replace(/\s\S*$/, '') + '…' : (t || 'New chat');
};

function groupProjects(list) {
  const now = Date.now(), day = 86400000;
  const groups = [['Today', []], ['Previous 7 days', []], ['Older', []]];
  for (const p of list) {
    const t = Date.parse((p.updated_at || p.created_at || '') + (/(Z|[+-]\d\d:?\d\d)$/.test(p.updated_at || '') ? '' : 'Z'));
    const age = now - (isNaN(t) ? 0 : t);
    groups[age < day ? 0 : age < 7 * day ? 1 : 2][1].push(p);
  }
  return groups.filter(([, xs]) => xs.length);
}

export class ChatApp extends Component {
  constructor(p) {
    super(p);
    this.state = {
      projects: null, project: null, items: [], busy: false, liveRun: null, phase: null, phaseAt: 0,
      phaseElapsed: 0, filesOpen: false, fileSel: null, filesStamp: 0, sidebarOpen: false, announce: '',
      error: '', seed: null, renaming: null, renameText: '', ws: null, loadingProject: false,
      filesWidth: readWidth(),
      models: [], defaultModel: '', paidModels: false, modelOpen: false,
      query: '', results: null, searching: false, cost: null,
      workspaces: null, wsId: null, maxWs: 3, shared: [], archived: null, showArchived: false,
      deleting: null, sharing: null, action: null, walletStamp: 0, boxOpen: false,
    };
    this.closer = null; this.ticker = null; this.lastWake = 0; this.wsTimer = null; this.stampTimer = null;
  }

  // ─── lifecycle ────────────────────────────────────────────────────────────
  async componentDidMount() {
    this.wsTimer = setInterval(() => { if (!document.hidden) this.refreshWs(); }, 45000);
    this.ticker = setInterval(() => {
      const { busy, phase, phaseAt } = this.state;
      if (busy && phaseAt && (phase === 'waking' || phase === 'starting' || phase === 'setting_up')) this.setState({ phaseElapsed: Math.round((Date.now() - phaseAt) / 1000) });
    }, 1000);
    await this.loadWorkspaces();
    await Promise.all([this.loadProjects(), this.loadShared(), this.loadModels()]);
    this.refreshWs();
    if (this.props.pid) this.open(this.props.pid);
  }

  componentDidUpdate(prev) {
    if (prev.pid !== this.props.pid) {
      if (this.props.pid) { if (String(this.state.project && this.state.project.id) !== String(this.props.pid)) this.open(this.props.pid); }
      else this.reset();
    }
  }

  componentWillUnmount() {
    if (this.closer) this.closer();
    clearInterval(this.ticker); clearInterval(this.wsTimer); clearTimeout(this.stampTimer);
  }

  reset() {
    if (this.closer) { this.closer(); this.closer = null; }
    this.setState({ project: null, items: [], busy: false, liveRun: null, phase: null, fileSel: null, error: '', action: null });
  }

  // ─── data ─────────────────────────────────────────────────────────────────
  async loadWorkspaces() {
    try {
      const r = await listWorkspaces();
      const ids = r.workspaces.map((w) => w.id);
      let want = null;
      try { want = parseInt(new URLSearchParams(location.search).get('workspace') || localStorage.getItem('uai.workspace') || '', 10); } catch (e) { /* */ }
      const wsId = ids.includes(want) ? want : (r.current || ids[0] || null);
      this.setState({ workspaces: r.workspaces, wsId, maxWs: r.max_per_user || 3 });
    } catch (e) { this.setState({ workspaces: [], error: e.message }); }
  }

  currentWs() { return (this.state.workspaces || []).find((w) => w.id === this.state.wsId) || null; }
  role() { const w = this.currentWs(); return w ? w.role : null; }
  canWrite() {
    const p = this.state.project;
    if (p) return !!(p.rights && p.rights.can_chat);
    // A brand-new account owns nothing yet: the first message is what creates its
    // workspace (the portal does it on demand), so let them type.
    if (this.state.workspaces && !this.state.workspaces.length) return true;
    return ['owner', 'member'].includes(this.role());
  }

  async switchWorkspace(id) {
    if (id === this.state.wsId) { route('/'); return; }
    try { localStorage.setItem('uai.workspace', String(id)); } catch (e) { /* */ }
    this.setState({ wsId: id, projects: null, archived: null, showArchived: false, ws: null });
    route('/');
    await this.loadProjects();
    this.refreshWs();
  }

  async newWorkspace(name) {
    const ws = await createWorkspace(name);
    this.setState((s) => ({ workspaces: [...(s.workspaces || []), { ...ws, role: 'owner' }] }));
    await this.switchWorkspace(ws.id);
  }

  async runSearch(q) {
    // Debounced in the input; this is the fetch. An empty or one-character query clears the
    // results rather than searching for everything, which is never what anyone means.
    this.setState({ query: q });
    if ((q || '').trim().length < 2) { this.setState({ results: null, searching: false }); return; }
    this.setState({ searching: true });
    try {
      const r = await searchChats(q.trim(), this.state.wsId);
      if (this.state.query.trim() === q.trim()) this.setState({ results: r.results || [], searching: false });
    } catch (e) { this.setState({ results: [], searching: false }); }
  }

  async loadCost(pid) {
    // What this chat has cost. The balance says whether you can keep going; this says what
    // the conversation in front of you is actually costing.
    if (!pid) { this.setState({ cost: null }); return; }
    try { this.setState({ cost: await projectCost(pid) }); }
    catch (e) { this.setState({ cost: null }); }
  }

  async loadModels() {
    // What this account may actually pick, straight from the portal — the same list the
    // router enforces. A picker offering a model the key would be refused on is worse than
    // no picker at all.
    try {
      const r = await listModels();
      this.setState({ models: r.models || [], defaultModel: r.default_model || '', paidModels: !!r.paid });
    } catch (e) { /* no picker rather than a wrong one */ }
  }

  async pickModel(model) {
    const pid = this.state.pid;
    this.setState({ modelOpen: false });
    if (!pid) return;
    try {
      const p = await setProjectModel(pid, model);
      // The agent link is restarted by the server when the model changes, so the next message
      // simply runs on the new one; nothing to do here but show it.
      this.setState((st) => ({ project: { ...(st.project || {}), model: p.model || model } }));
    } catch (e) { this.setState({ error: e.message }); }
  }

  modelMenu() {
    const { models, project, modelOpen, defaultModel, pid } = this.state;
    if (!pid || !(models || []).length) return null;
    const current = (project && project.model) || defaultModel || '';
    const label = (m) => (m || '').split('/').pop();
    return h('div', { className: 'model-menu' + (modelOpen ? ' on' : '') },
      h('button', { type: 'button', className: 'btn ghost', 'aria-expanded': modelOpen ? 'true' : 'false',
        title: current, onClick: () => this.setState({ modelOpen: !modelOpen }) },
        h(Icon, { name: 'spark', size: 14 }),
        h('span', { className: 'hide-sm' }, ' ' + (label(current) || 'Model'))),
      modelOpen ? h('div', { className: 'model-pop', role: 'menu' },
        h('p', { className: 'muted small' }, 'Which model answers in this chat. Switching takes effect on your next message.'),
        (models || []).map((m) => h('button', {
          key: m, type: 'button', role: 'menuitemradio', 'aria-checked': m === current ? 'true' : 'false',
          className: 'model-item' + (m === current ? ' on' : ''),
          onClick: () => this.pickModel(m) },
          h('span', { className: 'model-item-name' }, label(m)),
          h('span', { className: 'model-item-host' }, (m || '').split('/')[0]))),
        !this.state.paidModels ? h('p', { className: 'muted small' },
          'Add credit to unlock the rest of the catalogue.') : null) : null);
  }

  async loadShared() {
    try { this.setState({ shared: await sharedProjects() }); } catch (e) { /* optional */ }
  }

  async loadArchived() {
    if (!this.state.wsId) return;
    try { this.setState({ archived: await listProjects(this.state.wsId, true) }); } catch (e) { this.setState({ error: e.message }); }
  }

  async refreshWs() {
    if (!this.state.wsId) return;
    try {
      const ws = await workspaceState(this.state.wsId);
      const was = this.state.ws && this.state.ws.sleep_state;
      this.setState((s) => ({ ws, workspaces: (s.workspaces || []).map((w) => (w.id === ws.id ? { ...w, ...ws, role: w.role, owner_email: w.owner_email } : w)) }));
      // images from the workspace that failed while it slept can load now
      if (ws.sleep_state === 'AWAKE' && was && was !== 'AWAKE') window.dispatchEvent(new Event('uai-workspace-awake'));
      if (this.props.onWsState) this.props.onWsState(ws.sleep_state || '', ws.name || '');
    } catch (e) { /* the top bar keeps its last state */ }
  }

  async loadProjects() {
    if (!this.state.wsId) { this.setState({ projects: [] }); return; }
    try { this.setState({ projects: await listProjects(this.state.wsId) }); } catch (e) { this.setState({ projects: [], error: e.message }); }
  }

  open(pid) {
    // Creating a chat both changes the route and opens it; share one load, not two.
    if (this.opening && this.opening.pid === String(pid)) return this.opening.p;
    const p = this._open(pid).finally(() => { if (this.opening && this.opening.p === p) this.opening = null; });
    this.opening = { pid: String(pid), p };
    return p;
  }

  async _open(pid) {
    if (this.closer) { this.closer(); this.closer = null; }
    this.setState({ loadingProject: true, error: '', sidebarOpen: false });
    let data;
    try { data = await getProject(pid); } catch (e) { this.setState({ loadingProject: false, error: e.message }); return; }
    if (String(this.props.pid) !== String(pid)) return;            // navigated away meanwhile
    const live = data.live || {};
    const busy = BUSY.has(live.state);
    this.loadCost(data && data.id);
    this.setState({ project: data, items: fromHistory(data.messages), busy, liveRun: busy ? live.run : null,
      phase: busy ? live.phase : null, phaseAt: busy ? Date.now() : 0, loadingProject: false,
      fileSel: null, filesStamp: this.state.filesStamp + 1 });
    document.title = `${data.title} — eutonomous`;
    if (data.workspace && data.workspace !== this.state.wsId && (this.state.workspaces || []).some((w) => w.id === data.workspace)) {
      try { localStorage.setItem('uai.workspace', String(data.workspace)); } catch (e) { /* */ }
      this.setState({ wsId: data.workspace, archived: null, showArchived: false });
      this.loadProjects(); this.refreshWs();
    }
    this.closer = streamProject(pid, live.resume_after || live.seq || 0, (ev) => this.onEvent(pid, ev), () => {});
  }

  onEvent(pid, ev) {
    if (String(pid) !== String(this.state.project && this.state.project.id)) return;
    switch (ev.type) {
      case 'hello': {
        const busy = BUSY.has(ev.state);
        this.setState({ busy, liveRun: busy ? ev.run : null, phase: busy ? ev.phase : null });
        return;
      }
      case 'phase':
        this.setState((s) => ({ phase: ev.phase, busy: true, liveRun: ev.run || s.liveRun,
          phaseAt: s.phase === ev.phase ? s.phaseAt : Date.now() - (ev.elapsed || 0) * 1000,
          phaseElapsed: ev.elapsed || 0 }));
        return;
      case 'user':
        if (this.state.items.some((m) => m.role === 'user' && m.run != null && m.run === (ev.run_id || ev.run))) return;
        this.setState((s) => ({ items: applyEvent(s.items, ev), busy: true, liveRun: ev.run_id || ev.run, phase: s.phase || 'preparing', phaseAt: s.phaseAt || Date.now() }));
        return;
      case 'run_finished':
        window.dispatchEvent(new Event('uai-workspace-awake'));
        this.setState((s) => ({ items: applyEvent(s.items, ev), busy: false, liveRun: null, phase: null,
          announce: ev.status === 'done' ? 'Response complete' : `Response ${ev.status}`, filesStamp: s.filesStamp + 1 }));
        this.touchProject();
        this.refreshWs();
        if (ev.status !== 'done') this.setState((s) => ({ walletStamp: s.walletStamp + 1 }));
        return;
      case 'tool_update':
        if (ev.status === 'completed' && (ev.kind === 'file_write' || ev.kind === 'file_edit' || ev.kind === 'shell')) this.bumpFiles();
        break;
      default:
    }
    this.setState((s) => ({ items: applyEvent(s.items, ev) }));
  }

  bumpFiles() {
    if (!this.state.filesOpen) return;
    clearTimeout(this.stampTimer);
    this.stampTimer = setTimeout(() => this.setState((s) => ({ filesStamp: s.filesStamp + 1 })), 800);
  }

  touchProject() {
    const p = this.state.project; if (!p) return;
    const now = new Date().toISOString();
    this.setState((s) => ({ projects: (s.projects || []).map((x) => (x.id === p.id ? { ...x, updated_at: now } : x))
      .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at))) }));
  }

  // ─── actions ──────────────────────────────────────────────────────────────
  async send(text, attachments) {
    this.setState({ error: '' });
    let p = this.state.project;
    try {
      if (!p) {
        const hadNoWorkspace = this.state.workspaces && !this.state.workspaces.length;
        p = await createProject(titleFrom(text || (attachments[0] && attachments[0].name) || 'New chat'), 'auto', this.state.wsId);
        this.setState((s) => ({ projects: [p, ...(s.projects || [])] }));
        // the very first message creates the person's workspace: pick it up so the
        // switcher, the orb and the credit chip stop saying "No workspace"
        if (hadNoWorkspace) { await this.loadWorkspaces(); this.refreshWs(); this.setState((s) => ({ walletStamp: s.walletStamp + 1 })); }
        route('/p/' + p.id);
        await this.open(p.id);
      }
      // a chat created by attaching a file first is still called "New chat": name it now
      if (p.title === 'New chat' && text && !this.state.items.some((m) => m.role === 'user')) {
        const title = titleFrom(text);
        updateProject(p.id, { title }).catch(() => {});
        this.setState((s) => ({ project: s.project && s.project.id === p.id ? { ...s.project, title } : s.project,
          projects: (s.projects || []).map((x) => (x.id === p.id ? { ...x, title } : x)) }));
      }
      this.setState({ busy: true, phase: 'preparing', phaseAt: Date.now(), phaseElapsed: 0 });
      await sendMessage(p.id, text, attachments);
    } catch (e) {
      if (e.code) this.setState({ action: e.code, busy: false, phase: null, walletStamp: this.state.walletStamp + 1 });
      else this.setState({ error: e.message, busy: false, phase: null });
    }
  }

  // A new chat needs a project before files can be uploaded into it.
  async ensureProject() {
    if (this.state.project) return this.state.project.id;
    const p = await createProject('New chat', 'auto', this.state.wsId);
    this.setState((s) => ({ projects: [p, ...(s.projects || [])] }));
    route('/p/' + p.id);
    await this.open(p.id);
    return p.id;
  }

  stop() { if (this.state.project) cancelTurn(this.state.project.id).catch(() => {}); }

  async answer(cbid, payload) {
    try {
      await answerCallback(this.state.project.id, { callback_id: cbid, ...payload });
      this.setState((s) => ({ items: markAnswered(s.items, cbid) }));
    } catch (e) { this.setState({ error: e.message }); }
  }

  prewake() {
    const ws = this.state.ws;
    if (!ws || ws.sleep_state !== 'SLEEPING' || Date.now() - this.lastWake < 60000) return;
    this.lastWake = Date.now();
    wakeWs(ws.id).then(() => this.refreshWs()).catch(() => {});
    if (this.props.onWsState) this.props.onWsState('WAKING', ws.name || '');
  }

  openFile(path) {
    this.setState({ filesOpen: true, fileSel: path });
  }

  async rename(p) {
    const title = this.state.renameText.trim();
    this.setState({ renaming: null });
    if (!title || title === p.title) return;
    try {
      await updateProject(p.id, { title });
      this.setState((s) => ({ projects: s.projects.map((x) => (x.id === p.id ? { ...x, title } : x)),
        project: s.project && s.project.id === p.id ? { ...s.project, title } : s.project }));
    } catch (e) { this.setState({ error: e.message }); }
  }

  async archive(p) {
    try {
      await archiveProject(p.id);
      this.setState((s) => ({ projects: (s.projects || []).filter((x) => x.id !== p.id),
        archived: s.archived ? [{ ...p, archived: true }, ...s.archived] : s.archived }));
      if (this.state.project && this.state.project.id === p.id) route('/');
    } catch (e) { this.setState({ error: e.message }); }
  }

  async restore(p) {
    try {
      await restoreProject(p.id);
      this.setState((s) => ({ archived: (s.archived || []).filter((x) => x.id !== p.id),
        projects: [{ ...p, archived: false }, ...(s.projects || [])] }));
    } catch (e) { this.setState({ error: e.message }); }
  }

  async removeChat(p, withFiles) {
    await deleteProject(p.id, withFiles);
    this.setState((s) => ({ deleting: null,
      projects: (s.projects || []).filter((x) => x.id !== p.id),
      archived: s.archived ? s.archived.filter((x) => x.id !== p.id) : s.archived }));
    if (this.state.project && this.state.project.id === p.id) route('/');
  }

  startResize(e) {
    e.preventDefault();
    const startX = e.clientX, startW = this.state.filesWidth;
    const move = (ev) => this.setState({ filesWidth: Math.min(Math.max(startW + (startX - ev.clientX), 300), Math.round(window.innerWidth * 0.7)) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      try { localStorage.setItem('uai.filesWidth', String(this.state.filesWidth)); } catch (err) { /* */ } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }

  // ─── view ─────────────────────────────────────────────────────────────────
  chatItem(p, { archived = false, shared = false } = {}) {
    const { project, renaming, renameText } = this.state;
    const r = p.rights || {};
    if (renaming === p.id) {
      return h('form', { key: p.id, className: 'side-rename', onSubmit: (e) => { e.preventDefault(); this.rename(p); } },
        h('input', { value: renameText, ref: (el) => el && el.focus(), 'aria-label': 'Chat name',
          onInput: (e) => this.setState({ renameText: e.target.value }), onBlur: () => this.rename(p),
          onKeyDown: (e) => { if (e.key === 'Escape') this.setState({ renaming: null }); } }));
    }
    return h('div', { key: p.id, className: 'side-item' + (project && p.id === project.id ? ' on' : '') + (archived ? ' archived' : '') },
      archived ? h('span', { className: 'side-link', title: p.title }, p.title)
        : h('a', { href: '/p/' + p.id, className: 'side-link', title: shared ? `${p.title} — shared by ${p.shared_by}` : p.title }, p.title),
      r.can_manage ? h('span', { className: 'side-acts' },
        archived
          ? h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Restore', title: 'Restore', onClick: () => this.restore(p) }, h(Icon, { name: 'retry', size: 14 }))
          : h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Rename', title: 'Rename',
            onClick: () => this.setState({ renaming: p.id, renameText: p.title }) }, h(Icon, { name: 'pencil', size: 14 })),
        archived ? null : h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Archive', title: 'Archive', onClick: () => this.archive(p) }, h(Icon, { name: 'archive', size: 14 })),
        h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Delete', title: 'Delete', onClick: () => this.setState({ deleting: p }) }, h(Icon, { name: 'close', size: 14 }))) : null);
  }

  sidebar() {
    const { projects, workspaces, wsId, maxWs, shared, archived, showArchived } = this.state;
    const fresh = workspaces && !workspaces.length;      // no workspace yet: first run
    const canCreate = fresh || WRITE_ROLES.includes(this.role());
    return h('nav', { className: 'side', 'aria-label': 'Chats' },
      h(WorkspaceSwitcher, { workspaces, current: wsId, max: maxWs,
        onSwitch: (id) => { this.setState({ sidebarOpen: false }); this.switchWorkspace(id); },
        onCreate: (name) => this.newWorkspace(name) }),
      h(CreditChip, { stamp: this.state.walletStamp }),
      canCreate ? h('button', { type: 'button', className: 'new-chat', onClick: () => { route('/'); this.setState({ sidebarOpen: false }); } },
        h(Icon, { name: 'plus' }), ' New chat') : h('p', { className: 'side-note' }, 'You can read the chats in this workspace.'),
      h('div', { className: 'side-search' },
        h(Icon, { name: 'search', size: 14 }),
        h('input', { type: 'search', value: this.state.query, placeholder: 'Search chats',
          'aria-label': 'Search chats',
          onInput: (e) => {
            const v = e.target.value;
            clearTimeout(this._searchT);
            this._searchT = setTimeout(() => this.runSearch(v), 220);
            this.setState({ query: v });
          } })),
      this.state.results !== null ? h('div', { className: 'side-list' },
        this.state.searching && !this.state.results.length ? h('p', { className: 'side-note' }, 'Searching…')
          : !this.state.results.length ? h('p', { className: 'side-note' }, 'Nothing matches that.')
          : this.state.results.map((r) => h('button', {
              key: r.id, type: 'button', className: 'side-item search-hit',
              onClick: () => { this.setState({ sidebarOpen: false, query: '', results: null }); route('/p/' + r.id); } },
              h('span', { className: 'search-hit-title' }, r.title || 'New chat'),
              r.snippet ? h('span', { className: 'search-hit-snip' }, r.snippet) : null))) :
      h('div', { className: 'side-list' },
        projects === null ? h('div', { className: 'side-skel' }, [0, 1, 2, 3].map((i) => h('span', { key: i })))
          : !projects.length ? h('p', { className: 'muted side-empty' }, canCreate ? 'Your chats appear here.' : 'No chats here yet.')
          : groupProjects(projects).map(([label, xs]) => h('div', { key: label, className: 'side-group' },
            h('h4', null, label), xs.map((p) => this.chatItem(p)))),
        (shared || []).length ? h('div', { className: 'side-group' },
          h('h4', null, 'Shared with me'), shared.map((p) => this.chatItem(p, { shared: true }))) : null,
        wsId ? h('div', { className: 'side-group side-archive' },
          h('button', { type: 'button', className: 'side-toggle', 'aria-expanded': showArchived ? 'true' : 'false',
            onClick: () => { const open = !showArchived; this.setState({ showArchived: open }); if (open && archived === null) this.loadArchived(); } },
            h('span', { className: 'chev' + (showArchived ? ' open' : '') }), ' Archived'),
          showArchived ? (archived === null ? h('div', { className: 'side-skel' }, h('span', null))
            : !archived.length ? h('p', { className: 'muted side-empty' }, 'Nothing archived.')
            : archived.map((p) => this.chatItem(p, { archived: true }))) : null) : null));
  }

  // "Not a black box": the same workspace, opened directly. The URLs come from the portal
  // (state.url); the editor and terminal live behind the workspace's own sign-in.
  boxMenu() {
    const s = this.state;
    const ws = s.ws;
    if (!ws || !ws.url || ['PENDING', 'PROVISIONING'].includes(ws.status)) return null;
    const asleep = ws.sleep_state !== 'AWAKE';
    const items = [
      ['code', 'Editor', ws.url + '/vscode/'],
      ['shell', 'Terminal', ws.url + '/term/'],
      ['external', 'What it serves', ws.url],
    ];
    return h('div', { className: 'box-menu' + (s.boxOpen ? ' on' : '') },
      h('button', { type: 'button', className: 'btn ghost', 'aria-expanded': s.boxOpen ? 'true' : 'false',
        onClick: () => this.setState({ boxOpen: !s.boxOpen }) },
        h(Icon, { name: 'folder' }), h('span', { className: 'hide-sm' }, ' Open sandbox')),
      s.boxOpen ? h('div', { className: 'box-pop', role: 'menu' },
        h('p', { className: 'muted small' }, asleep
          ? 'Your workspace is asleep — opening one of these wakes it.'
          : 'The same machine the agent works in.'),
        items.map(([icon, label, href]) => h('a', { key: label, href, target: '_blank', rel: 'noopener',
          onClick: () => { this.setState({ boxOpen: false }); if (asleep) this.prewake(); } },
          h(Icon, { name: icon, size: 14 }), ' ', label)),
        h('a', { href: '/workspaces', onClick: () => this.setState({ boxOpen: false }) },
          h(Icon, { name: 'spark', size: 14 }), ' Databases, tools and settings')) : null);
  }

  render(props, s) {
    const pid = s.project && s.project.id;
    const title = s.project ? s.project.title : 'New chat';
    const rights = (s.project && s.project.rights) || {};
    const canWrite = this.canWrite();
    const viewerNote = s.project && rights.chat_viewer ? 'This chat was shared with you to read.'
      : 'You can view this workspace. Ask its owner for member access to chat.';
    return h('div', { className: 'chat-app' + (s.sidebarOpen ? ' side-open' : '') + (s.filesOpen ? ' files-open' : ''),
      style: { '--files-w': s.filesWidth + 'px' } },
      this.sidebar(),
      h('div', { className: 'side-scrim', onClick: () => this.setState({ sidebarOpen: false }) }),
      h('main', { className: 'main' },
        h('header', { className: 'main-head' },
          h('button', { type: 'button', className: 'icon-btn only-mobile', 'aria-label': 'Open chats',
            onClick: () => this.setState({ sidebarOpen: true }) }, h(Icon, { name: 'menu', size: 18 })),
          h('h1', { className: 'main-title', title }, title),
          s.project && !canWrite ? h('span', { className: 'role-badge' }, rights.chat_viewer ? 'Shared with you' : 'View only') : null,
          pid && rights.can_manage ? h('button', { type: 'button', className: 'btn ghost', onClick: () => this.setState({ sharing: s.project }) },
            h(Icon, { name: 'external' }), h('span', { className: 'hide-sm' }, ' Share')) : null,
          this.modelMenu(),
          s.cost && s.cost.cost_eur > 0 ? h('span', { className: 'chat-cost',
            title: `${s.cost.turns} turns · ${s.cost.tokens_in + s.cost.tokens_out} tokens` },
            '€' + s.cost.cost_eur.toFixed(s.cost.cost_eur < 0.1 ? 4 : 2)) : null,
          this.boxMenu(),
          pid ? h('button', { type: 'button', className: 'btn ghost files-btn' + (s.filesOpen ? ' on' : ''),
            'aria-pressed': s.filesOpen ? 'true' : 'false',
            onClick: () => this.setState({ filesOpen: !s.filesOpen, filesStamp: s.filesStamp + 1 }) },
            h(Icon, { name: 'files' }), h('span', null, ' Files')) : null),
        s.action ? h('div', { className: 'action-wrap' }, h(ActionCard, { code: s.action })) : null,
        s.error ? h('div', { className: 'banner err', role: 'alert' }, s.error,
          h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Dismiss', onClick: () => this.setState({ error: '' }) }, h(Icon, { name: 'close' }))) : null,
        s.loadingProject ? h('div', { className: 'feed-wrap' }, h('div', { className: 'feed' }, h('div', { className: 'feed-inner' },
          h('div', { className: 'skel' }, h('span', null), h('span', null), h('span', { className: 'short' }))))) :
        h(Feed, { items: s.items, pid, busy: s.busy, liveRun: s.liveRun, phase: s.phase, phaseElapsed: s.phaseElapsed,
          announce: s.announce, emptyTitle: s.project ? 'What should we work on?' : null,
          me: props.me, authors: s.project && s.project.authors, canChat: canWrite,
          empty: canWrite ? null : h('p', { className: 'muted empty-note' }, 'Pick a chat on the left.'),
          onAnswer: (id, pl) => this.answer(id, pl), onOpenFile: (p) => this.openFile(p),
          onRetry: (u) => this.send(u.content, u.attachments || []),
          onSuggest: (text) => this.setState({ seed: { text, at: Date.now() } }) }),
        canWrite ? h(Composer, { pid, busy: s.busy, seed: s.seed, autoFocus: true,
          ensureProject: () => this.ensureProject(),
          onSend: (t, a) => this.send(t, a), onStop: () => this.stop(), onPrewake: () => this.prewake(),
          hint: s.ws && s.ws.lock ? (this.role() === 'owner' ? 'This workspace is locked until you add credit.' : 'This workspace is paused: its owner needs to add credit.')
            : (s.workspaces && !s.workspaces.length) ? 'Your first message sets up your workspace — that takes a minute.'
            : s.ws && s.ws.sleep_state === 'SLEEPING' ? 'Your workspace is asleep; it wakes when you send.' : '' })
          : h('div', { className: 'composer viewer' }, h('div', { className: 'composer-box viewer-box' }, h(Icon, { name: 'read' }), ' ', viewerNote))),
      s.filesOpen && pid ? h('div', { className: 'files-col' },
        h('div', { className: 'files-grip', onPointerDown: (e) => this.startResize(e), role: 'separator', 'aria-orientation': 'vertical', 'aria-label': 'Resize files panel' }),
        h(FilesPanel, { pid, selected: s.fileSel, stamp: s.filesStamp, onSelect: (p) => this.setState({ fileSel: p }),
          onClose: () => this.setState({ filesOpen: false }) })) : null,
      h(DeleteChatDialog, { project: s.deleting, onClose: () => this.setState({ deleting: null }), onDelete: (p, f) => this.removeChat(p, f) }),
      h(ShareDialog, { project: s.sharing, onClose: () => this.setState({ sharing: null }) }));
  }
}

function readWidth() {
  try { return Math.max(300, parseInt(localStorage.getItem('uai.filesWidth') || '440', 10) || 440); } catch (e) { return 440; }
}
